export const DASH = {
  font: "'DM Sans', system-ui, sans-serif",
  fontMono: "'DM Mono', monospace",
  bg: "var(--tz-bg)",
  card: "var(--tz-surface)",
  border: "var(--tz-border)",
  borderLight: "var(--tz-border-subtle)",
  text: "var(--tz-text)",
  textSecondary: "var(--tz-text-secondary)",
  textMuted: "var(--tz-text-muted)",
  accent: "#111110",
  accentSoft: "rgba(17,17,16,0.04)",
  shadow: "none",
  radius: "16px",
  radiusSm: "12px",
};

export const STATUS_COLORS = {
  PENDING: { bg: "transparent", color: "#6b6b68", bar: "#a3a39f" },
  SCHEDULED: { bg: "transparent", color: "#6b6b68", bar: "#6b6b68" },
  IN_PROGRESS: { bg: "transparent", color: "#111110", bar: "#111110" },
  COMPLETED: { bg: "transparent", color: "#111110", bar: "#111110" },
  CANCELLED: { bg: "transparent", color: "#9b9b97", bar: "#c4c4c0" },
  POSTPONED: { bg: "transparent", color: "#9b9b97", bar: "#c4c4c0" },
};

export const TYPE_COLORS = [
  "#111110", "#3d3d3a", "#6b6b68", "#9b9b97",
  "#c4c4c0", "#d8d8d4", "#e8e8e6", "#f0f0ee",
];

export const GLOBAL_DASH_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&family=DM+Mono:wght@400;500&display=swap');

  .dash-root {
    font-family: 'DM Sans', system-ui, sans-serif;
    color: #111110;
    max-width: 1320px;
    margin: 0 auto;
    animation: dashFadeIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) forwards;
  }

  @keyframes dashFadeIn {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  @keyframes dashShimmer {
    0%   { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }

  .dash-skeleton {
    background: linear-gradient(90deg, #f0f0ee 25%, #e8e8e6 50%, #f0f0ee 75%);
    background-size: 200% 100%;
    animation: dashShimmer 1.8s ease infinite;
    border-radius: 8px;
  }

  .dash-hero {
    margin-bottom: 40px;
    padding-bottom: 32px;
    border-bottom: 1px solid #e8e8e6;
  }

  .dash-hero-date {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #a3a39f;
    margin-bottom: 12px;
  }

  .dash-hero-title {
    font-size: clamp(2rem, 4vw, 2.75rem);
    font-weight: 600;
    letter-spacing: -0.04em;
    line-height: 1.05;
    color: #111110;
    margin: 0;
  }

  .dash-hero-sub {
    margin: 10px 0 0;
    font-size: 15px;
    font-weight: 400;
    color: #6b6b68;
    letter-spacing: -0.01em;
  }

  .dash-hero-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    margin-top: 28px;
  }

  .dash-hero-stat {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .dash-hero-stat-val {
    font-family: 'DM Mono', monospace;
    font-size: 1.5rem;
    font-weight: 500;
    letter-spacing: -0.03em;
    color: #111110;
    line-height: 1;
  }

  .dash-hero-stat-lbl {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #a3a39f;
  }

  .dash-section-label {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #a3a39f;
    margin-bottom: 16px;
  }

  .dash-metrics-row {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 0;
    border: 1px solid #e8e8e6;
    border-radius: 16px;
    overflow: hidden;
    margin-bottom: 32px;
    background: #ffffff;
  }

  .dash-metric-cell {
    padding: 24px 20px;
    border-right: 1px solid #e8e8e6;
    transition: background 0.2s;
  }

  .dash-metric-cell:last-child { border-right: none; }
  .dash-metric-cell:hover { background: #fafaf8; }

  .dash-metric-val {
    font-family: 'DM Mono', monospace;
    font-size: 1.625rem;
    font-weight: 500;
    letter-spacing: -0.03em;
    color: #111110;
    line-height: 1;
  }

  .dash-metric-lbl {
    margin-top: 8px;
    font-size: 12px;
    font-weight: 500;
    color: #6b6b68;
  }

  .dash-metric-delta {
    margin-top: 6px;
    font-size: 11px;
    font-weight: 500;
    color: #a3a39f;
  }

  .dash-metric-delta--up { color: #2d6a4f; }
  .dash-metric-delta--down { color: #9b4d4d; }

  @media (max-width: 1024px) {
    .dash-metrics-row { grid-template-columns: repeat(3, 1fr); }
    .dash-metric-cell:nth-child(3) { border-right: none; }
    .dash-metric-cell:nth-child(4),
    .dash-metric-cell:nth-child(5) { border-top: 1px solid #e8e8e6; }
    .dash-metric-cell:nth-child(4) { border-right: 1px solid #e8e8e6; }
    .dash-metric-cell:nth-child(5) { border-right: none; }
  }

  @media (max-width: 640px) {
    .dash-metrics-row { grid-template-columns: 1fr 1fr; }
    .dash-metric-cell { border-right: 1px solid #e8e8e6; border-bottom: 1px solid #e8e8e6; }
    .dash-metric-cell:nth-child(2n) { border-right: none; }
    .dash-metric-cell:nth-child(5) { border-bottom: none; }
    .dash-hero-meta { gap: 16px; }
  }
`;
